import { Helmet } from "react-helmet-async";
import SEOConfig from "./SEOConfig";

// Google truncates title links at roughly 580px, about 60 Cyrillic characters. Past that the
// brand suffix is what gets cut, so drop it and keep the page's own words whole.
export const MAX_TITLE_LENGTH = 60;

export const formatTitle = (title, { siteName, titleTemplate }) => {
  if (!title) return siteName;
  const withBrand = titleTemplate.replace("%s", title);
  return withBrand.length > MAX_TITLE_LENGTH ? title : withBrand;
};

const SEO = ({
  title,
  // A complete <title> that skips the brand template, for the home page, which leads with the brand.
  fullTitle,
  description,
  image,
  url,
  schema,
  canonical,
  type = "website",
  keywords = [],
  lang = "uk",
  // max-image-preview:large lets Google show large images in Discover and image results.
  robots = "index, follow, max-image-preview:large",
  breadcrumbs,
  children,
  // Article-only: the byline and dates of a news post.
  author,
  publishedTime,
  modifiedTime,
}) => {
  const {
    siteName,
    titleTemplate,
    defaultDescription,
    defaultImage,
    defaultImageWidth,
    defaultImageHeight,
    siteUrl,
    sportKeywords,
  } = SEOConfig;

  // Use provided values or defaults
  const metaTitle = fullTitle || title || siteName;
  const formattedTitle = fullTitle || formatTitle(title, { siteName, titleTemplate });
  const isArticle = type === "article";
  const metaKeywords = [...new Set([...sportKeywords, ...keywords].filter(Boolean))];
  const metaDescription = description || defaultDescription;
  const metaImage = image || `${siteUrl}${defaultImage}`;
  // Dimensions are only known for our own default image; content images vary.
  const isDefaultImage = !image;
  const metaUrl = url || siteUrl;
  const canonicalUrl = canonical || metaUrl;

  // Create breadcrumb schema if provided
  let breadcrumbSchema = null;
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: crumb.url,
      })),
    };
  }

  return (
    <Helmet htmlAttributes={{ lang }}>
      {/* Basic meta tags */}
      <title>{formattedTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="keywords" content={metaKeywords.join(", ")} />
      {isArticle && author && <meta name="author" content={author} />}
      <meta name="robots" content={robots} />

      {/* Open Graph tags */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={metaImage} />
      {isDefaultImage && <meta property="og:image:width" content={String(defaultImageWidth)} />}
      {isDefaultImage && <meta property="og:image:height" content={String(defaultImageHeight)} />}
      <meta property="og:image:alt" content={metaTitle} />
      <meta property="og:locale" content="uk_UA" />
      {isArticle && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {isArticle && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Schema.org structured data */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}

      {/* Breadcrumb schema if provided */}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}

      {children}
    </Helmet>
  );
};

export default SEO;
